import { ProjectFormData } from '../../interfaces/ui/addProjectModal';
import { IqeProject } from '../../interfaces/ui';
import { IqeProjectOperationRequest } from '../../interfaces/api/requests/saveProject.request';
import { IqeApiOperation } from '../../interfaces/api/requests/shared.request';
import type {
  IqeProjectAction,
  EnhancementTestPlanSelection,
} from '../../interfaces/ui';
import { toSystemValue } from '../../utils';
import { isEnhanceOperation } from '../../utils/projectExecutionLifecycle';
import {
  buildWriteTestPlan,
  buildReadTestPlan,
  buildEmptyReadTestPlan,
} from '../sharedDto/testPlan.dto';

/**
 * Maps the internal IqeProjectAction + project flags to the granular
 * IqeApiOperation string required by the new IQE contract.
 */
function toApiOperation(
  operation: IqeProjectAction,
  loginNotRequired: boolean,
  hasReadTestPlan: boolean,
): IqeApiOperation {
  switch (operation) {
    case 'CREATE':
      return loginNotRequired ? 'CREATE_NOLOGIN' : 'CREATE_LOGIN';
    case 'CREATE_RERUN':
      return loginNotRequired ? 'CREATE_NOLOGIN_RERUN' : 'CREATE_LOGIN_RERUN';
    case 'GENERATE':
      return hasReadTestPlan
        ? 'GENERATE_WITHTESTPLAN'
        : 'GENERATE_WITHOUTTESTPLAN';
    case 'GENERATE_RERUN':
      return hasReadTestPlan
        ? 'GENERATE_WITHTESTPLAN_RERUN'
        : 'GENERATE_WITHOUTTESTPLAN_RERUN';
    case 'EXECUTE':
      return 'EXECUTE';
    case 'EXECUTE_RERUN':
      return 'EXECUTE_RERUN';
    case 'ENHANCE':
      return 'ENHANCE';
    case 'ENHANCE_RERUN':
      return 'ENHANCE_RERUN';
  }
}

/**
 * Maps a stored IqeProject (from API list state) to a full IqeProjectOperationRequest
 * for action operations: RERUN, GENERATE, EXECUTE, ENHANCE.
 */
export function mapIqeProjectToActionRequest(
  project: IqeProject,
  operation: Exclude<IqeProjectAction, 'CREATE'>,
  userPrompt: string = '',
  enhancementSelection?: EnhancementTestPlanSelection,
): IqeProjectOperationRequest {
  const system = toSystemValue(project.projectMappingType);
  const readTestPlan = project.readTestPlan;
  const hasReadTestPlan =
    system === 'ado'
      ? Boolean('suiteId' in readTestPlan && readTestPlan.suiteId)
      : Boolean('subFolderId' in readTestPlan && readTestPlan.subFolderId);

  return {
    projectId: project.id,
    projectName: project.name,
    appUrl: project.appUrl,
    environment: project.environment,
    flowId: project.flowId,
    flowName: project.flowName,
    repositoryConfig: project.repositoryConfig,
    frontendRepositoryName: project.frontendRepositoryName,
    frontendRepositoryUrl: project.frontendRepositoryUrl,
    frontendRepositoryBranch: project.frontendRepositoryBranch,
    // playwrightTestRepositoryUrl: project.playwrightTestRepositoryUrl,
    operation: toApiOperation(
      operation,
      project.loginNotRequired ?? false,
      hasReadTestPlan,
    ),
    writeTestPlan: project.writeTestPlan,
    readTestPlan:
      isEnhanceOperation(operation)
        ? enhancementSelection
          ? buildReadTestPlan(
              toSystemValue(enhancementSelection.projectMappingType),
              enhancementSelection.mappedProjectId,
              enhancementSelection.mappedProjectName,
              enhancementSelection.mappedProjectOrg,
              enhancementSelection.testPlanId,
              enhancementSelection.testPlanName,
              enhancementSelection.testSuiteId,
              enhancementSelection.testSuiteName,
            )
            : buildEmptyReadTestPlan(system)
        : project.readTestPlan,
    userPrompt,
  };
}

export function mapFormToSaveProjectRequest(
  formData: ProjectFormData,
): IqeProjectOperationRequest {
  const system = toSystemValue(formData.projectMappingType);

  return {
    projectId: '',
    projectName: formData.projectName,
    appUrl: formData.appUrl,
    environment: formData.environment,
    flowId: formData.flowId,
    flowName: formData.flowName,
    operation: toApiOperation('CREATE', formData.loginNotRequired, false),
    repositoryConfig: formData.repositoryConfig,
    frontendRepositoryName: formData.frontendRepositoryName,
    frontendRepositoryUrl: formData.frontendRepositoryUrl,
    frontendRepositoryBranch: formData.frontendRepositoryBranch,
    // playwrightTestRepositoryUrl: formData.playwrightTestRepositoryUrl,
    writeTestPlan: buildWriteTestPlan(
      system,
      formData.mappedProjectId,
      formData.mappedProjectName,
      formData.mappedProjectOrg,
      formData.testPlanId,
      formData.testPlanName,
      formData.testSuiteId,
      formData.testSuiteName,
    ),
    readTestPlan: buildReadTestPlan(
      system,
      formData.mappedProjectId,
      formData.mappedProjectName,
      formData.mappedProjectOrg,
      formData.existingTestPlanId,
      formData.existingTestPlanName,
      formData.existingTestSuiteId,
      formData.existingTestSuiteName,
    ),
  };
}
